/**
 * PhoneLink — klickbare Rufnummer mit Click-to-Call (feature/crm2-3cx-integration).
 *
 * Klick löst POST /v2/admin/3cx/click-to-call aus (wählt von der eigenen
 * 3CX-Extension aus dem Profil). Solange 3CX nicht konfiguriert ist (503,
 * "3CX-Konsolenzugang steht noch aus" — siehe documentation/3cx-integration.md)
 * oder der Admin keine Extension gesetzt hat (422), fällt der Klick sauber
 * auf einen normalen `tel:`-Link zurück statt einen Fehler zu zeigen.
 *
 * Nutzung: <PhoneLink number={user.mobile} dark={dark} />
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';
import { BRAND } from '../lib/theme';
import { v2ThreeCx, threeCxErrorStatus } from '../lib/threeCx';
import { toast } from '../lib/toast';

interface PhoneLinkProps {
  number: string | null | undefined;
  dark?: boolean;
  /** Show the phone icon before the number (default: false — sidebars already have a label). */
  showIcon?: boolean;
  style?: React.CSSProperties;
  /** Beschriftung statt der Nummer (z. B. „Kunde anrufen"). */
  label?: React.ReactNode;
  /**
   * Wird nach dem Klick aufgerufen, wenn der Anruf angestoßen wurde
   * (3CX Click-to-Call erfolgreich ODER stiller tel:-Fallback bei 503/422, nicht bei Fehler). Default: nichts.
   */
  onDial?: () => void;
}

function telHref(number: string): string {
  return `tel:${number.replace(/[^\d+]/g, '')}`;
}

export function PhoneLink({ number, dark = false, showIcon = false, style, label, onDial }: PhoneLinkProps) {
  const { t: tr } = useTranslation();
  const [dialing, setDialing] = useState(false);

  if (!number) return <span style={{ color: dark ? '#667085' : '#98a2b3' }}>—</span>;

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (dialing) return;
    setDialing(true);
    let dialed = false;
    try {
      await v2ThreeCx.clickToCall(number);
      toast(tr('phone.dialing', { number }), 'success');
      dialed = true;
    } catch (err) {
      const status = threeCxErrorStatus(err);
      // 503 (3CX XAPI not configured yet) / 422 (no own extension) /
      // network error / anything else → fall back to a plain tel: link so
      // the click still does *something* useful.
      if (status !== 503 && status !== 422) {
        toast(tr('phone.dial_failed'), 'warning');
      } else {
        dialed = true; // stiller tel:-Fallback gilt als gestarteter Anruf
      }
      window.location.href = telHref(number);
    } finally {
      setDialing(false);
      // Nur bei Erfolg bzw. stillem tel:-Fallback; bei echtem Fehler steht schon "Anruf konnte nicht eingeleitet werden".
      if (dialed) onDial?.();
    }
  };

  return (
    <a
      href={telHref(number)}
      onClick={handleClick}
      aria-busy={dialing}
      title={tr('phone.click_to_call')}
      style={{
        color: BRAND, textDecoration: 'none', wordBreak: 'break-all',
        display: 'inline-flex', alignItems: 'center', gap: 5,
        cursor: dialing ? 'wait' : 'pointer', opacity: dialing ? 0.6 : 1,
        ...style,
      }}
    >
      {showIcon && <Icon.Phone c={BRAND} />}
      {label ?? number}
    </a>
  );
}
